import { Button, NumberInput, Group, Textarea, Select } from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { useTenantStore } from "./tenantStore";
import { modifyCredits } from "../api";

function creditTypeOptions(tenant, formatCreditType) {
  const seen = new Set();
  const options = [];
  const add = (value) => {
    const type = value || "default";
    if (seen.has(type)) return;
    seen.add(type);
    options.push({
      value: type,
      label: formatCreditType ? formatCreditType(type) : type === "default" ? "Default" : type,
    });
  };
  add("default");
  for (const batch of tenant?.credits || []) add(batch.creditType);
  return options;
}

export default function AddCreditsForm({ tenant, formatCreditType, creditsHint }) {
  const [isLoading, setIsLoading] = useState(false);
  const updateTenantStore = useTenantStore((state) => state.updateTenant);
  const typeOptions = creditTypeOptions(tenant, formatCreditType);
  const defaultType =
    typeOptions.find((o) => o.value.startsWith("sponsored:"))?.value || "default";

  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      credits: 0,
      note: "",
      creditType: defaultType,
      validDays: "31",
    },
  });

  return (
    <form
      onSubmit={form.onSubmit((values) => {
        setIsLoading(true);
        modifyCredits(tenant.id, {
          ...values,
          credits: Number(values.credits),
          validDays: Number(values.validDays),
        })
          .then((data) => {
            updateTenantStore(tenant.id, data); // Update global state with API response
            notifications.show({ title: "Credits added", color: "green" });
            setIsLoading(false);
          })
          .catch((error) => {
            notifications.show({
              title: "Failed to add credits",
              message: error.message,
              color: "red",
            });
            setIsLoading(false);
          });
      })}
    >
      <Group align="flex-end">
        <NumberInput
          label="Credits"
          description={creditsHint}
          {...form.getInputProps("credits")}
          key={form.key("credits")}
          radius="md"
        />

        <Select
          label="Type"
          description={
            typeOptions.some((o) => o.value.startsWith("sponsored:"))
              ? "Trial / partner types only spend on that marketplace."
              : undefined
          }
          {...form.getInputProps("creditType")}
          key={form.key("creditType")}
          radius="md"
          data={typeOptions}
        />

        <Select
          label="Validity"
          {...form.getInputProps("validDays")}
          key={form.key("validDays")}
          data={[
            { value: "31", label: "31d" },
            { value: "92", label: "92d" },
            { value: "366", label: "366d" },
          ]}
          radius="md"
        />
      </Group>
      <Textarea
        label="Note"
        minRows={2}
        maxRows={5}
        autosize
        {...form.getInputProps("note")}
        key={form.key("note")}
      />
      <Button mt="sm" disabled={isLoading} type="submit">
        Apply
      </Button>
    </form>
  );
}
